import { get, set } from 'idb-keyval';
import { Store, MergeableStore } from 'tinybase';
import { createCustomPersister } from 'tinybase/persisters';

import { isProd } from '~/client/config/appConfig';

import { StoreCrypto } from '../StoreCrypto';
import { jsonParseWithUndefined, jsonStringWithUndefined, onIgnoredError } from './helpers';
import { PersisterStoreName, PersistedContentType } from './types';

export const initCustomIndexedDbPersister = async (
  store: Store | MergeableStore,
  storeName: PersisterStoreName,
  storeCrypto?: StoreCrypto,
) => {
  const debugLog = (message: string, ...args: any[]) =>
    !isProd && console.debug(`[${storeName} encrypted: ${!!storeCrypto}] ${message}`, ...args);

  const getDecrypted = async (): Promise<PersistedContentType | undefined> => {
    if (!storeCrypto) {
      return;
    }
    try {
      const encryptedData = await get<ArrayBuffer>(storeName);
      const decryptedDataString = encryptedData && (await storeCrypto.decrypt(encryptedData));
      return decryptedDataString ? jsonParseWithUndefined(decryptedDataString) : undefined;
    } catch (err) {
      console.error(err, `[${storeName}] Failed to decrypt data`);
    }
  };

  const getPersisted = async (): Promise<PersistedContentType> => {
    const data = storeCrypto ? await getDecrypted() : await get<PersistedContentType>(storeName);

    debugLog(data ? 'Loaded from device' : 'No persisted data found', data);

    return data || store.getContent();
  };

  const setEncrypted = async (data: PersistedContentType) => {
    if (!storeCrypto) {
      return false;
    }
    try {
      const encryptedData = await storeCrypto.encrypt(jsonStringWithUndefined(data));
      await set(storeName, encryptedData);
      return true;
    } catch (err) {
      console.error(err, 'Failed to encrypt data');
      return false;
    }
  };

  const setPersisted = async (getContent: () => PersistedContentType): Promise<void> => {
    const data = getContent();

    let success = true;

    if (storeCrypto) {
      success = await setEncrypted(data);
    } else {
      await set(storeName, data);
    }
    if (success) {
      debugLog('Saved to device', data);
    }
  };

  const persister = createCustomPersister(
    store,
    getPersisted,
    setPersisted,
    () => undefined,
    () => undefined,
    onIgnoredError(storeName),
    3, // StoreOrMergeableStore,
  );

  console.log('loading store', storeName);

  await persister.load(store.getContent());
  await persister.startAutoSave();

  console.log('store initialised', storeName);

  return persister;
};
